import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  ScanLine, Camera, Package, Search, ExternalLink, PlusCircle,
  Loader2, AlertTriangle, CheckCircle, Keyboard, X, Globe, Cpu,
  Wifi, Link2, Tag, ShoppingBag, Shield, ShieldAlert, ShieldCheck, ShieldX, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import BarcodeScanner from '../components/BarcodeScanner';

const decodeHtml = (s) => {
  if (!s) return s;
  const el = document.createElement('textarea');
  el.innerHTML = s;
  return el.value;
};

export default function BarcodeScannerPage() {
  const navigate = useNavigate();
  const [manualInput, setManualInput] = useState('');
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scannedValue, setScannedValue] = useState('');
  const [customers, setCustomers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [webLookup, setWebLookup] = useState(null);
  const [webLoading, setWebLoading] = useState(false);
  const inputRef = useRef(null);

  const [newAsset, setNewAsset] = useState({
    name: '', asset_type: 'device', serial_number: '',
    manufacturer: '', model: '', status: 'active', customer_id: ''
  });

  useEffect(() => {
    loadCustomers();
    inputRef.current?.focus();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      setCustomers(Array.isArray(res.data) ? res.data : res.data.customers || []);
    } catch { /* ignore */ }
  };

  const handleLookup = async (value) => {
    const q = (value || '').trim();
    if (!q) return;
    
    // If scanned value is a CipherOne asset URL, navigate directly
    const assetUrlMatch = q.match(/\/assets\/([a-f0-9-]{36})/);
    if (assetUrlMatch) {
      navigate(`/assets/${assetUrlMatch[1]}`);
      return;
    }
    
    setScannedValue(q);
    setShowCreate(false);
    setLoading(true);
    setResults([]);
    setProducts([]);
    setWebLookup(null);
    setWebLoading(false);
    try {
      const res = await api.get('/assets/lookup-by-scan', { params: { q, include_products: true } });
      const localResults = res.data.results || [];
      const localProducts = res.data.products || [];
      setResults(localResults);
      setProducts(localProducts);

      const totalLocal = localResults.length + localProducts.length;
      setScanHistory(prev => {
        const entry = { value: q, time: new Date().toLocaleTimeString(), found: totalLocal };
        return [entry, ...prev].slice(0, 20);
      });

      // Auto-trigger web lookup if no local results
      if (totalLocal === 0) {
        setWebLoading(true);
        try {
          const webRes = await api.post('/scan/web-lookup', { q });
          setWebLookup(webRes.data);
          // Pre-fill create form with web lookup data
          if (webRes.data) {
            setNewAsset(prev => ({
              ...prev,
              serial_number: q,
              manufacturer: webRes.data.manufacturer || webRes.data.vendor || '',
              name: webRes.data.product_name || '',
            }));
          }
        } catch {
          // silently fail web lookup
        }
        setWebLoading(false);
      }
    } catch {
      toast.error('Lookup failed');
    }
    setLoading(false);
  };

  const handleCameraScan = (value) => {
    setManualInput(value);
    handleLookup(value);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    handleLookup(manualInput);
  };

  // USB barcode scanners fire keystrokes fast — detect rapid input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && manualInput.trim()) {
      e.preventDefault();
      handleLookup(manualInput);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    if (!newAsset.name.trim()) { toast.error('Asset name is required'); return; }
    setCreating(true);
    try {
      const payload = { ...newAsset };
      if (payload.customer_id) {
        const cust = customers.find(c => c.id === payload.customer_id);
        if (cust) payload.customer_name = cust.company_name;
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const res = await api.post('/assets', payload);
      toast.success(`Asset "${res.data.name}" created`);
      setShowCreate(false);
      setResults(prev => [{ ...res.data, _type: 'asset' }, ...prev]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail || 'Failed to create asset');
    }
    setCreating(false);
  };

  const statusColor = (s) => ({
    active: 'bg-emerald-100 text-emerald-700',
    maintenance: 'bg-amber-100 text-amber-700',
    inactive: 'bg-gray-100 text-gray-600',
    retired: 'bg-red-100 text-red-700'
  })[s] || 'bg-gray-100 text-gray-600';

  const totalResults = results.length + products.length;

  const WebLookupIcon = ({ type }) => {
    switch (type) {
      case 'mac_address': return <Wifi className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
      case 'service_tag': return <Cpu className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
      case 'express_service_code': return <Tag className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
      case 'upc_barcode': return <ShoppingBag className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
      case 'url': return <Link2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
      default: return <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />;
    }
  };

  const WarrantyCard = ({ warranty }) => {
    const statusConfig = {
      active: { icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Active' },
      expiring_soon: { icon: ShieldAlert, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Expiring Soon' },
      expired: { icon: ShieldX, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Expired' },
      check_manually: { icon: Shield, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Check Manually' },
      unknown: { icon: Shield, color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Unknown' },
    };
    const cfg = statusConfig[warranty.warranty_status] || statusConfig.unknown;
    const StatusIcon = cfg.icon;

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
      catch { return dateStr; }
    };

    return (
      <div className={`p-3 rounded-lg border ${cfg.bg} ${cfg.border}`} data-testid="warranty-info-card">
        <div className="flex items-start gap-2">
          <StatusIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${cfg.color}`} />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm font-semibold ${cfg.color}`}>Warranty: {cfg.label}</span>
              {warranty.source && (
                <Badge variant="outline" className="text-[10px]">
                  {warranty.source.includes('api') ? 'via API' : 'via web'}
                </Badge>
              )}
              {warranty.api_mode && (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">API keys active</Badge>
              )}
            </div>
            {warranty.manufacturer && (
              <p className="text-xs text-gray-600">Manufacturer: <strong>{warranty.manufacturer}</strong></p>
            )}
            {warranty.model && (
              <p className="text-xs text-gray-600">Model: <strong>{warranty.model}</strong></p>
            )}
            {warranty.warranty_end && (
              <p className="text-xs text-gray-600 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Expires: <strong>{formatDate(warranty.warranty_end)}</strong>
                {warranty.days_remaining != null && (
                  <span className={`ml-1 ${warranty.days_remaining > 90 ? 'text-emerald-600' : warranty.days_remaining > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    ({warranty.days_remaining > 0 ? `${warranty.days_remaining} days left` : `${Math.abs(warranty.days_remaining)} days past`})
                  </span>
                )}
              </p>
            )}
            {warranty.warranty_type && (
              <p className="text-xs text-gray-600">Type: {warranty.warranty_type}</p>
            )}
            {warranty.ship_date && (
              <p className="text-xs text-gray-600">Ship Date: {formatDate(warranty.ship_date)}</p>
            )}
            {warranty.product_line && (
              <p className="text-xs text-gray-600">Product Line: {warranty.product_line}</p>
            )}
            {warranty.support_url && (
              <a href={warranty.support_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                <ExternalLink className="h-3 w-3" /> View on support site
              </a>
            )}
            {/* Manual check links when warranty info not found on page */}
            {warranty.support_links?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {warranty.support_links.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full border bg-white hover:bg-gray-50 text-gray-600 hover:text-blue-600">
                    <ExternalLink className="h-2.5 w-2.5" /> {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="barcode-scanner-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Barcode Scanner</h1>
        <p className="text-gray-600 text-sm mt-1">Scan asset tags, serial numbers, or product barcodes to look up items</p>
      </div>

      {/* Scanner Input Area */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Camera Scan */}
            <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 sm:w-48">
              <Camera className="h-8 w-8 text-gray-400" />
              <p className="text-xs text-gray-500 text-center">Camera Scan</p>
              <BarcodeScanner
                onScan={handleCameraScan}
                buttonLabel="Open Camera"
                buttonVariant="default"
                buttonSize="sm"
              />
            </div>

            {/* Manual / USB Input */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2">
                <Keyboard className="h-4 w-4 text-gray-500" />
                <Label className="text-sm font-medium">Manual Entry / USB Scanner</Label>
              </div>
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scan barcode here or type serial number..."
                  className="flex-1 font-mono text-base h-12"
                  autoFocus
                  data-testid="barcode-manual-input"
                />
                <Button type="submit" disabled={loading || !manualInput.trim()} className="h-12 px-6" data-testid="barcode-lookup-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  {loading ? '' : 'Look Up'}
                </Button>
              </form>
              <div className="text-xs text-gray-400">
                <p className="text-xs text-gray-400">
                  USB barcode scanners automatically type into this field. Press Enter or click Look Up to search.
                  When no local match is found, the system automatically searches the internet.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {scannedValue && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600">Results for:</span>
              <code className="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono font-semibold" data-testid="scan-query-display">{scannedValue}</code>
            </div>
            {!loading && totalResults === 0 && (
              <Button size="sm" onClick={() => {
                setNewAsset({ name: '', asset_type: 'device', serial_number: scannedValue, manufacturer: '', model: '', status: 'active', customer_id: '' });
                setShowCreate(true);
              }} data-testid="scan-create-new-btn">
                <PlusCircle className="h-4 w-4 mr-1.5" /> Create New Asset
              </Button>
            )}
          </div>

          {loading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin mr-3" /> Searching assets and products...
              </CardContent>
            </Card>
          )}

          {!loading && totalResults === 0 && !showCreate && (
            <>
              {/* Web Lookup Loading */}
              {webLoading && (
                <Card>
                  <CardContent className="flex items-center justify-center py-12 text-gray-500">
                    <Globe className="h-5 w-5 animate-pulse mr-3 text-blue-500" />
                    <span>No local match — searching the internet...</span>
                  </CardContent>
                </Card>
              )}

              {/* Web Lookup Results */}
              {!webLoading && webLookup && (
                <Card data-testid="web-lookup-results">
                  <CardHeader className="pb-2 px-4 pt-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-500" />
                      Internet Lookup Results
                      <Badge variant="outline" className="text-[10px] ml-1">{webLookup.scan_type?.replace(/_/g, ' ')}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* Main finding */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="web-lookup-details">
                      <div className="flex items-start gap-2">
                        <WebLookupIcon type={webLookup.scan_type} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-blue-900">{webLookup.details || 'No additional information found'}</p>
                          {webLookup.vendor && (
                            <p className="text-xs text-blue-700 mt-1">Vendor: <strong>{webLookup.vendor}</strong></p>
                          )}
                          {webLookup.manufacturer && webLookup.manufacturer !== webLookup.vendor && (
                            <p className="text-xs text-blue-700 mt-1">Manufacturer: <strong>{webLookup.manufacturer}</strong></p>
                          )}
                          {webLookup.product_name && (
                            <p className="text-xs text-blue-700 mt-1">Product: <strong>{webLookup.product_name}</strong></p>
                          )}
                          {webLookup.converted_service_tag && (
                            <p className="text-xs text-blue-700 mt-1">Service Tag: <code className="bg-blue-100 px-1 rounded font-mono">{webLookup.converted_service_tag}</code></p>
                          )}
                          {webLookup.mac_address && (
                            <p className="text-xs text-blue-700 mt-1">MAC: <code className="bg-blue-100 px-1 rounded font-mono">{webLookup.mac_address}</code></p>
                          )}
                          {webLookup.category && (
                            <p className="text-xs text-blue-700 mt-1">Category: {webLookup.category}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Warranty Information */}
                    {webLookup.warranty && webLookup.warranty.warranty_found && (
                      <WarrantyCard warranty={webLookup.warranty} />
                    )}
                    {webLookup.warranty && !webLookup.warranty.warranty_found && webLookup.warranty.warranty_status === 'not_found' && (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Shield className="h-4 w-4" />
                          <span>Warranty info not available via auto-lookup. Check manufacturer sites below.</span>
                        </div>
                      </div>
                    )}

                    {/* Support Links */}
                    {webLookup.support_links?.length > 0 && (
                      <div className="space-y-1.5" data-testid="web-lookup-links">
                        <p className="text-xs font-medium text-gray-600">Look up on manufacturer sites:</p>
                        <div className="flex flex-wrap gap-2">
                          {webLookup.support_links.map((link, i) => (
                            <a
                              key={i}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-600 transition-colors"
                              data-testid={`web-lookup-link-${i}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                              {link.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Single support URL */}
                    {webLookup.support_url && !webLookup.support_links?.length && (
                      <a
                        href={webLookup.support_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid="web-lookup-support-url"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View on manufacturer support site
                      </a>
                    )}

                    {/* URL type — open button */}
                    {webLookup.type === 'url' && webLookup.url && (
                      <a
                        href={webLookup.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                        data-testid="web-lookup-open-url"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open scanned URL
                      </a>
                    )}

                    {/* Search URL fallback */}
                    {webLookup.search_url && (
                      <a
                        href={webLookup.search_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600"
                        data-testid="web-lookup-search-url"
                      >
                        <Search className="h-3 w-3" />
                        Search Google for this code
                      </a>
                    )}

                    <Separator />

                    {/* Create asset from findings */}
                    <Button size="sm" onClick={() => {
                      const w = webLookup.warranty || {};
                      setNewAsset({
                        name: webLookup.product_name || w.model || '',
                        asset_type: webLookup.scan_type === 'mac_address' ? 'network' : 'device',
                        serial_number: webLookup.converted_service_tag || webLookup.service_tag || scannedValue,
                        manufacturer: webLookup.manufacturer || webLookup.vendor || w.manufacturer || '',
                        model: w.model || '',
                        status: 'active',
                        customer_id: '',
                        warranty_expiry: w.warranty_end ? w.warranty_end.split('T')[0] : '',
                      });
                      setShowCreate(true);
                    }} data-testid="web-lookup-create-asset-btn">
                      <PlusCircle className="h-4 w-4 mr-1.5" /> Create Asset from Findings
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Fallback: no web results either */}
              {!webLoading && !webLookup && (
                <Card>
                  <CardContent className="flex flex-col items-center py-10 text-gray-500">
                    <AlertTriangle className="h-10 w-10 text-amber-400 mb-3" />
                    <p className="font-medium text-base">No matching items found</p>
                    <p className="text-sm mt-1">This barcode/serial number isn't in the system yet</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Asset Results */}
          {!loading && results.length > 0 && (
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4" /> Assets ({results.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2" data-testid="scan-asset-results">
                  {results.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/assets/${asset.id}`)}
                      data-testid={`scan-asset-${asset.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">{decodeHtml(asset.name)}</span>
                          <Badge className={`text-[10px] ${statusColor(asset.status)}`}>{asset.status}</Badge>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 ml-6 flex flex-wrap gap-x-3">
                          {asset.serial_number && <span>SN: <span className="font-mono">{asset.serial_number}</span></span>}
                          {asset.asset_type && <span>{asset.asset_type}</span>}
                          {asset.customer_name && <span>{asset.customer_name}</span>}
                          {asset.manufacturer && <span>{asset.manufacturer}</span>}
                          {asset.model && <span>{asset.model}</span>}
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Results */}
          {!loading && products.length > 0 && (
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4" /> Products ({products.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2" data-testid="scan-product-results">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate('/products-services')}
                      data-testid={`scan-product-${product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">{decodeHtml(product.name)}</span>
                          {product.is_active !== false && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Active</Badge>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 ml-6 flex flex-wrap gap-x-3">
                          {product.category && <span>{product.category}</span>}
                          {product.unit_price != null && <span>${Number(product.unit_price).toFixed(2)}</span>}
                          {product.sku && <span>SKU: {product.sku}</span>}
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Create Asset Form */}
          {showCreate && (
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <PlusCircle className="h-4 w-4" /> Create New Asset
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <form onSubmit={handleCreateAsset} className="space-y-3" data-testid="barcode-create-form">
                  <div className="text-xs bg-blue-50 text-blue-700 p-2.5 rounded-lg flex items-center gap-2">
                    <ScanLine className="h-4 w-4 flex-shrink-0" />
                    Serial number pre-filled from scan: <strong className="font-mono">{scannedValue}</strong>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Asset Name *</Label>
                      <Input value={newAsset.name} onChange={(e) => setNewAsset(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dell Latitude 5540" required data-testid="barcode-create-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Serial Number</Label>
                      <Input value={newAsset.serial_number} onChange={(e) => setNewAsset(p => ({ ...p, serial_number: e.target.value }))} className="font-mono" data-testid="barcode-create-serial" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={newAsset.asset_type} onValueChange={(v) => setNewAsset(p => ({ ...p, asset_type: v }))}>
                        <SelectTrigger className="h-9" data-testid="barcode-create-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="device">Device</SelectItem>
                          <SelectItem value="workstation">Workstation</SelectItem>
                          <SelectItem value="server">Server</SelectItem>
                          <SelectItem value="network">Network</SelectItem>
                          <SelectItem value="software">Software</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Manufacturer</Label>
                      <Input value={newAsset.manufacturer} onChange={(e) => setNewAsset(p => ({ ...p, manufacturer: e.target.value }))} placeholder="e.g. Dell" data-testid="barcode-create-manufacturer" />
                    </div>
                    <div>
                      <Label className="text-xs">Model</Label>
                      <Input value={newAsset.model} onChange={(e) => setNewAsset(p => ({ ...p, model: e.target.value }))} placeholder="e.g. Latitude 5540" data-testid="barcode-create-model" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Customer</Label>
                    <Select value={newAsset.customer_id || '__none'} onValueChange={(v) => setNewAsset(p => ({ ...p, customer_id: v === '__none' ? '' : v }))}>
                      <SelectTrigger className="h-9" data-testid="barcode-create-customer"><SelectValue placeholder="Select customer (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">No customer</SelectItem>
                        {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowCreate(false)} data-testid="barcode-create-cancel">
                      <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={creating} data-testid="barcode-create-submit">
                      {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PlusCircle className="h-4 w-4 mr-1" />}
                      {creating ? 'Creating...' : 'Create Asset'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Create button when results exist but user wants to add new */}
          {!loading && totalResults > 0 && !showCreate && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={() => {
                setNewAsset({ name: '', asset_type: 'device', serial_number: scannedValue, manufacturer: '', model: '', status: 'active', customer_id: '' });
                setShowCreate(true);
              }} data-testid="scan-create-anyway-btn">
                <PlusCircle className="h-4 w-4 mr-1.5" /> Not what you're looking for? Create New Asset
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm text-gray-600">Recent Scans</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-1" data-testid="scan-history">
              {scanHistory.map((entry, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50 cursor-pointer text-sm"
                  onClick={() => { setManualInput(entry.value); handleLookup(entry.value); }}
                >
                  <div className="flex items-center gap-2">
                    <ScanLine className="h-3.5 w-3.5 text-gray-400" />
                    <code className="font-mono text-xs">{entry.value}</code>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span>{entry.found} result{entry.found !== 1 ? 's' : ''}</span>
                    <span>{entry.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
